import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faHeart, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartEmpty } from '@fortawesome/free-regular-svg-icons';
import { useToast } from './Toast';
import { addProductToCart, toggleProductFavorite, isGuestFavorite, getStoredUser } from '../utils/guestStore';
import './ProductCard.css';

const ProductCard = ({ product, onCartUpdate, onProductClick, highlight = false }) => {
  const toast = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const cardRef = useRef(null);

  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlight]);

  useEffect(() => {
    if (!getStoredUser()) {
      setIsFavorite(isGuestFavorite(product.id));
    }
  }, [product.id]);

  const handleAddToCart = async (e) => {
    e?.stopPropagation();
    try {
      const result = await addProductToCart(product, quantity);
      if (result.ok) {
        toast.success(`Added ${quantity} to cart`);
        if (onCartUpdate) onCartUpdate();
      } else {
        toast.error(result.message || 'Failed to add to cart');
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      toast.error('Error adding to cart');
    }
  };

  const handleAddToFavorites = async (e) => {
    e.stopPropagation();
    try {
      const result = await toggleProductFavorite(product, isFavorite);
      if (result.ok) {
        setIsFavorite(result.isFavorite);
        toast.success(result.isFavorite ? 'Added to favourites' : 'Removed from favourites');
      } else {
        toast.error(result.message || 'Failed to update favorites');
      }
    } catch (err) {
      console.error('Favorites error:', err);
      toast.error('Error updating favorites');
    }
  };

  const handleCardClick = () => {
    if (onProductClick) onProductClick(product);
  };

  const decreaseQty = (e) => {
    e.stopPropagation();
    setQuantity((q) => Math.max(1, q - 1));
  };

  const increaseQty = (e) => {
    e.stopPropagation();
    setQuantity((q) => q + 1);
  };

  return (
    <div
      ref={cardRef}
      className={`product-card${highlight ? ' highlight' : ''}${onProductClick ? ' clickable' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onProductClick ? handleCardClick : undefined}
      role={onProductClick ? 'button' : undefined}
      tabIndex={onProductClick ? 0 : undefined}
      onKeyDown={
        onProductClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
    >
      <div className="product-image">
        <img src={product.image} alt={product.name} loading="lazy" />
        <div className="product-icons">
          <button
            type="button"
            className="icon-btn cart-btn"
            onClick={handleAddToCart}
            title="Add to Cart"
          >
            <FontAwesomeIcon icon={faShoppingCart} />
          </button>
          <button
            type="button"
            className={`icon-btn heart-btn ${isFavorite ? 'active' : ''}`}
            onClick={handleAddToFavorites}
            title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          >
            <FontAwesomeIcon icon={isFavorite ? faHeart : faHeartEmpty} />
          </button>
        </div>
      </div>
      <div className="product-info">
        <h4>{product.name}</h4>
        <p>{product.description}</p>
        <div className="product-meta">
          <span className="price">Rs. {parseFloat(product.price).toFixed(2)}</span>
        </div>
        <div className="product-qty-row">
          <div className="qty-control" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="qty-btn" onClick={decreaseQty} aria-label="Decrease quantity">
              <FontAwesomeIcon icon={faMinus} />
            </button>
            <span className="qty-value">{quantity}</span>
            <button type="button" className="qty-btn" onClick={increaseQty} aria-label="Increase quantity">
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          {(isHovered || !onProductClick) && (
            <button type="button" className="add-btn" onClick={handleAddToCart}>
              Add to cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
